import { Injectable, inject } from '@angular/core';
import { AccountStore } from '../stores';
import { Storage } from './storage';
import { LoggerService } from './logger.service';
import type { MerchantAccount, RawMerchantAccount } from '../models';

/**
 * AccountService — business logic for account management.
 *
 * Everything here keys on `accountId` (the `MerchantAccountUser` row id). The raw `_id` a
 * login payload carries is the *merchant* id and must never be used as an account key.
 *
 * Responsibilities:
 * - Initialise accounts after login or app startup
 * - Own which account is active, and persist that choice
 *
 * Switching the active account from inside the app is deliberately NOT handled here: it
 * requires a fresh account-scoped session (a re-login), which only `AuthService` may install.
 * See `AuthService.switchAccount`. The previous optimistic client-only swap that lived here
 * moved just the active `accountId` and left every Gozem Money request mismatched against the
 * old merchant's session — the cause of error 1032 — so it was removed (decision D4 resolved).
 *
 * Security:
 * - Full account data in memory only (`AccountStore`)
 * - Only the selected `accountId` persisted (non-sensitive)
 * - All account data cleared on logout
 */
@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private readonly accountStore = inject(AccountStore);
  private readonly storage = inject(Storage);
  private readonly logger = inject(LoggerService);

  /**
   * Initialise accounts after login or session restore.
   *
   * Activation order, first match wins:
   *   1. the account the caller asked for (the one the user just picked)
   *   2. the account persisted from a previous visit
   *   3. the merchant's default account
   *   4. the first account
   *
   * Step 2 fails softly by design. Before this change the persisted id was a *merchant* id,
   * so a returning user carries a value that now matches nothing — it simply falls through
   * to the default rather than needing a migration.
   *
   * @param accounts - raw rows from the login response or from localStorage
   * @param preSelectedAccountId - `accountId` to activate, when the user chose one
   */
  initializeAccounts(
    accounts: readonly RawMerchantAccount[] | null | undefined,
    preSelectedAccountId?: string,
  ): void {
    this.accountStore.setAccounts(accounts);

    const normalized = this.accountStore.accounts();

    if (normalized.length === 0) {
      this.logger.error('[AccountService] No usable accounts provided for initialization');
      return;
    }

    const byId = (id: string | null | undefined): MerchantAccount | null =>
      id ? (normalized.find((acc) => acc.accountId === id) ?? null) : null;

    const accountToActivate =
      byId(preSelectedAccountId) ??
      byId(this.storage.getSelectedAccountId()) ??
      this.accountStore.defaultAccount();

    if (accountToActivate) {
      this.setActiveAccount(accountToActivate.accountId);
    } else {
      this.logger.error('[AccountService] Failed to determine account to activate');
    }
  }

  /**
   * Record the active account in the store and in localStorage.
   *
   * The single writer for that pair — keeping them in sync anywhere else invites the two
   * drifting apart across a reload.
   */
  setActiveAccount(accountId: string): void {
    this.accountStore.setActiveAccount(accountId);
    this.storage.setSelectedAccountId(accountId);
  }

  /**
   * Get the currently active account
   */
  getActiveAccount(): MerchantAccount | null {
    return this.accountStore.activeAccount();
  }

  /**
   * Get all available accounts
   */
  getAllAccounts(): MerchantAccount[] {
    return this.accountStore.accounts();
  }

  /**
   * Check if user has multiple accounts
   */
  hasMultipleAccounts(): boolean {
    return this.accountStore.hasMultipleAccounts();
  }

  /**
   * Clear accounts from store and storage
   * Called on logout
   */
  clearAccounts(): void {
    this.accountStore.clearAccounts();
    this.storage.clearSelectedAccountId();
    this.logger.success('[AccountService] Accounts cleared');
  }
}
